import mustache from "mustache";
import { serviceProvider } from "../../../../../comun/provider/ServiceProvider";
import {
  FincaGanaderiaRequest,
  fx_createFincaGanaderiaRequest,
} from "../../../../../modulos/configuracion/ganaderia/request/FincaGanaderiaRequest";
import { FincaGanaderiaSaveService } from "../../../../../modulos/configuracion/ganaderia/services/FincaGanaderiaSaveService";
import { ClienteFincaBaseResponse } from "../../../../../modulos/terceros/response/ClienteFincaBaseResponse";
import { BaseCreateRender } from "../../../../base/BaseCreateRender";
import { IFormField } from "../../../../definitions/form/IFormField";
import { FormCardRender } from "../../../../implementations/card/FormCardRender";
import { InputNumberField } from "../../../../implementations/form/number/InputNumberField";
import template from "./ganaderiaConfiguracionModalTemplate.html";

mustache.parse(template);

/** Modal de configuración de ganadería de una finca (auto-montado sobre `body`).
 *  Hospeda un `FormCardRender` con los óptimos de la finca (crecimiento diario,
 *  días de ocupación y de descanso). Al guardar actualiza la finca activa y
 *  recarga la página. Se usa en dos modos:
 *  - **obligatorio** (`cancelable = false`): se abre al entrar a una finca de
 *    ganadería sin configurar; sin botón cancelar ni cierre por backdrop.
 *  - **cancelable** (`cancelable = true`): se abre desde el botón del menú; con
 *    botón cancelar y cierre por backdrop. */
export class GanaderiaConfiguracionModal extends BaseCreateRender {
  /** Id del elemento raíz auto-montado en `body`. */
  protected readonly elementId = "ganaderiaConfiguracionModal";

  /** Si el modal puede cerrarse sin guardar (backdrop + botón cancelar). */
  private readonly _cancelable: boolean;

  /** Card de formulario que edita la configuración de ganadería. */
  private readonly _formCard: FormCardRender<
    FincaGanaderiaRequest,
    ClienteFincaBaseResponse
  >;

  /**
   * @param finca      Finca a configurar (precarga el request).
   * @param cancelable Si el modal puede cerrarse sin guardar.
   */
  constructor(finca: ClienteFincaBaseResponse, cancelable: boolean) {
    super();
    this._cancelable = cancelable;
    const request = fx_createFincaGanaderiaRequest(finca);
    this._formCard = new FormCardRender<
      FincaGanaderiaRequest,
      ClienteFincaBaseResponse
    >(request, {
      cardHeader: {
        title: "Configuración de ganadería",
        subtitle: finca.nombreFinca,
      },
      additionalCardBodyCssClass: "sx-card-body--gap",
      createFormFields: (req) => this._createFields(req),
      saveService: new FincaGanaderiaSaveService(),
      submitText: "Guardar configuración",
      cancelAction: cancelable ? () => this.dispose() : undefined,
      onSubmitEnd: (finca) => this._onSaveEnd(finca),
    });
  }

  /** HTML del modal: overlay + contenido con el card del formulario renderizado. */
  protected getHtml(): string {
    return mustache.render(template, { card: this._formCard.render() });
  }

  /** Conecta el card, captura el overlay, cablea el cierre por backdrop (solo si
   *  es cancelable) y dispara la animación de apertura. */
  protected afterInit(element: HTMLDivElement): void {
    this._formCard.connect();
    this._overlay = element.querySelector<HTMLDivElement>(".sx-modal-overlay");
    if (this._cancelable) {
      this._overlay?.addEventListener("click", this._onOverlayClick);
    }
    requestAnimationFrame(() =>
      this._overlay?.classList.add("sx-modal-overlay--open"),
    );
  }

  /** Libera el listener del backdrop y el card antes de desmontar el modal. */
  protected beforeDispose(_element: HTMLDivElement): void {
    this._overlay?.removeEventListener("click", this._onOverlayClick);
    this._formCard.dispose();
  }

  /** Elemento overlay (backdrop) del modal (disponible tras `afterInit`). */
  private _overlay: HTMLDivElement | null = null;

  /** Cierra el modal solo si el click fue en el backdrop (no en el contenido). */
  private _onOverlayClick = (e: MouseEvent): void => {
    if (e.target === this._overlay) this.dispose();
  };

  /** Tras guardar: actualiza la finca activa con la devuelta, cierra y recarga. */
  private _onSaveEnd(finca: ClienteFincaBaseResponse): void {
    serviceProvider.getFincaSelectedService().set(finca);
    this.dispose();
    serviceProvider.getLayoutService().reload();
  }

  /** Campos del form: crecimiento diario + días de ocupación + días de descanso. */
  private _createFields(request: FincaGanaderiaRequest): IFormField[] {
    return [
      new InputNumberField<FincaGanaderiaRequest>(
        request,
        {
          label: "Crecimiento diario óptimo (g/día)",
          description: "Ganancia de peso diaria esperada por animal.",
          get: (r) => r.ganaderiaCrecimientoDiarioGr,
          set: (r, value) => {
            r.ganaderiaCrecimientoDiarioGr = value;
          },
        },
        { isRequired: true, min: 1 },
      ),
      new InputNumberField<FincaGanaderiaRequest>(
        request,
        {
          label: "Días de ocupación óptimo",
          description: "Días que los animales permanecen en un potrero.",
          get: (r) => r.ganaderiaDiasOcupacionOptimo,
          set: (r, value) => {
            r.ganaderiaDiasOcupacionOptimo = value;
          },
        },
        { isRequired: true, min: 1 },
      ),
      new InputNumberField<FincaGanaderiaRequest>(
        request,
        {
          label: "Días de descanso óptimo",
          description: "Días de recuperación de un potrero antes de reusarlo.",
          get: (r) => r.ganaderiaDiasDescansoOptimo,
          set: (r, value) => {
            r.ganaderiaDiasDescansoOptimo = value;
          },
        },
        { isRequired: true, min: 1 },
      ),
      new InputNumberField<FincaGanaderiaRequest>(
        request,
        {
          label: "Carga animal óptima (animales/ha)",
          description:
            "Referencia por defecto de animales por hectárea para los lotes de la finca.",
          get: (r) => r.ganaderiaAnimalesXHectareaOptimo,
          set: (r, value) => {
            r.ganaderiaAnimalesXHectareaOptimo = value;
          },
          decimal: true,
        },
        { isRequired: true, min: 0.01 },
      ),
    ];
  }
}
